import { Link } from "react-router-dom";
import { ArrowUpRight, Plus } from "lucide-react";
import { Eyebrow, Reveal } from "./Reveal";
import { scrollToId } from "../../lib/scroll";

const SERVICES = [
  {
    id: "S/01",
    imageSrc: "/media/corporate-wand-scan-office.jpg",
    imageAlt: "A technician sweeping an open office floor with a handheld detector",
    imagePos: "32% 52%",
    tag: "Reactive and scheduled",
    title: "Corporate TSCM inspection",
    text: "Authorised examination of offices, cabins, boardrooms and meeting spaces following a concern or as part of a security programme.",
    points: [
      "Offices, cabins and boardrooms",
      "Meeting and conference rooms",
      "Any room where sensitive matters are discussed",
      "Written report on completion",
    ],
    href: "/corporate-tscm",
  },
  {
    id: "S/02",
    imageSrc: "/media/boardroom-chair-wand-check.jpg",
    imageAlt: "A detector being run beneath boardroom chairs before a meeting",
    imagePos: "30% 60%",
    tag: "Before the meeting",
    title: "Preventive TSCM",
    text: "The space inspected close to the meeting rather than weeks ahead.",
    points: [
      "Board and audit committee sittings",
      "Transaction and investor discussions",
      "Hotel and conference venues",
      "Rented or temporary offices",
    ],
    href: "/preventive-tscm",
  },
  {
    id: "S/03",
    imageSrc: "/media/monitoring-spectrum-laptop.jpg",
    imageAlt: "Live spectrum on a laptop with a radio alongside, watched while a meeting runs",
    imagePos: "62% 48%",
    tag: "During the meeting",
    title: "Live meeting monitoring",
    text: "An add-on to an on-site inspection: monitoring of the radio frequencies from the room while the meeting runs.",
    points: [
      "Spectrum held under observation",
      "A small LAT team on site throughout",
      "For the discussions that matter most",
    ],
    note: "It does not replace the physical inspection, and it cannot detect a device that does not transmit.",
    href: "/live-meeting-monitoring",
  },
  {
    id: "S/04",
    imageSrc: "/media/annual-contract-schedule.jpg",
    imageAlt: "An annual planner on a boardroom table with inspection dates circled, equipment case alongside",
    tag: "Ongoing",
    title: "Annual contract",
    text: "A set number of inspections each year with priority response.",
    points: [
      "Scheduled sweeps through the year",
      "Priority callout when something happens, on terms agreed in the contract",
      "A record kept across the year's inspections",
    ],
  },
  {
    id: "S/05",
    imageSrc: "/media/residential-pole-camera-diptych.jpg",
    imageAlt: "Pole camera checks of curtains, air-conditioning and a chandelier in a residence",
    imagePos: "50% 42%",
    tag: "Residences",
    title: "Residential TSCM",
    text: "Available with a corporate engagement or on its own. Discreet inspection of executive homes, private floors, guest accommodation and staff-accessed areas.",
    points: [
      "Bedrooms, studies and private offices",
      "Rooms used by guests, staff and contractors included in the inspection",
      "Discreet scheduling around the household",
    ],
  },
  {
    id: "S/06",
    imageSrc: "/media/vehicle-etron-team-inspection.jpg",
    imageAlt: "The team inspecting a vehicle in a basement car park, doors open",
    imagePos: "50% 40%",
    tag: "Vehicles",
    title: "Vehicle TSCM",
    text: "Available with a corporate engagement or on its own. Cars and chauffeur-driven vehicles examined for tracking and in-cabin recording risks.",
    points: [
      "GPS and cellular tracking devices",
      "In-cabin audio and recording devices",
      "Inspection after servicing, valeting or loan of the vehicle",
      "Single vehicle or full fleet",
    ],
  },
];

export default function ServiceCards() {
  return (
    <section id="service-options" className="py-12 sm:py-16" data-testid="services-section">
      <div className="site-x">
        <Reveal>
          <Eyebrow>Services</Eyebrow>
          <h3 className="mt-4 max-w-2xl font-head font-extrabold tracking-tight h-sec">
            Choose the inspection that fits your situation
          </h3>
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-sub">
            Book according to your need — live monitoring can be added on for
            highly sensitive meetings.
          </p>
        </Reveal>

        <div className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {SERVICES.map(({ id, tag, title, text, points, note, href, imageSrc, imageAlt, imagePos }, i) => (
            <Reveal key={id} delay={(i % 3) * 0.08} className="h-full">
              <article
                data-testid={`service-card-${i + 1}`}
                className="group flex h-full flex-col border border-hairline bg-surface p-6 transition-[border-color,transform] duration-300 hover:-translate-y-1 hover:border-violet"
              >
                {imageSrc && (
                  <img
                    src={imageSrc}
                    alt={imageAlt}
                    loading="lazy"
                    style={imagePos ? { objectPosition: imagePos } : undefined}
                    className="-mx-6 -mt-6 mb-5 aspect-[16/9] w-[calc(100%+3rem)] max-w-none border-b border-hairline object-cover"
                    data-testid={`service-card-image-${i + 1}`}
                  />
                )}
                <div className="flex items-center justify-between gap-2">
                  <span className="rounded-full border border-violet/40 px-2.5 py-1 text-[0.5625rem] font-semibold uppercase tracking-[0.16em] text-violet">
                    {tag}
                  </span>
                  <span className="text-[0.625rem] uppercase tracking-[0.16em] text-sub">
                    {id}
                  </span>
                </div>
                <h4 className="mt-5 font-head text-xl font-bold leading-snug">{title}</h4>
                <p className="mt-2 text-base leading-relaxed text-sub">{text}</p>
                <ul className="mt-5 flex flex-1 flex-col gap-2 border-t border-hairline pt-5">
                  {points.map((point) => (
                    <li key={point} className="flex items-start gap-2 text-sm leading-snug text-sub">
                      <Plus size={11} className="mt-1 shrink-0 text-violet" />
                      {point}
                    </li>
                  ))}
                </ul>
                {note && (
                  <p className="mt-4 text-base leading-snug text-sub">{note}</p>
                )}
                {href ? (
                  <Link
                    to={href}
                    data-testid={`service-more-${i + 1}`}
                    className="mt-5 inline-flex items-center gap-1.5 text-[0.625rem] font-semibold uppercase tracking-[0.16em] text-violet transition-colors hover:text-violeth"
                  >
                    More about this service
                    <ArrowUpRight size={12} />
                  </Link>
                ) : (
                  <button
                    onClick={() => scrollToId("contact")}
                    data-testid={`service-more-${i + 1}`}
                    className="mt-5 inline-flex items-center gap-1.5 self-start rounded-full border border-ink/25 px-4 py-1.5 text-[0.625rem] font-semibold uppercase tracking-[0.16em] transition-colors hover:border-violet hover:text-violet"
                  >
                    Ask about this service
                    <ArrowUpRight size={12} />
                  </button>
                )}
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
